// src/pages/Chat.jsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { useVideoCall } from '../context/VideoCallContext';
import VideoCall from '../components/VideoCall';
import { Link } from 'react-router-dom';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  let date;
  try {
    if (typeof timestamp?.toDate === 'function') date = timestamp.toDate();
    else date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid date';
  } catch {
    return 'Invalid date';
  }
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString();
};

const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  let date;
  try {
    if (typeof timestamp?.toDate === 'function') date = timestamp.toDate();
    else date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
  } catch {
    return '';
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const getReactionCounts = (reactions) => {
  const counts = {};
  if (!reactions) return counts;
  Object.values(reactions).forEach((type) => {
    counts[type] = (counts[type] || 0) + 1;
  });
  return counts;
};

const useDebounced = (value, delay = 200) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

/**
 * Normalize an arbitrary lastMessage into a string preview.
 * lastMessage may be a string, an object with { text, imageUrl, type },
 * or null/undefined.
 */
const formatLastMessagePreview = (lastMessage) => {
  if (!lastMessage) return 'No messages yet';
  if (typeof lastMessage === 'string') {
    return lastMessage.length > 40
      ? lastMessage.substring(0, 37) + '...'
      : lastMessage;
  }
  if (typeof lastMessage === 'object') {
    if (lastMessage.imageUrl && !lastMessage.text) return '📷 Photo';
    if (lastMessage.text) {
      const t = String(lastMessage.text);
      return t.length > 40 ? t.substring(0, 37) + '...' : t;
    }
    if (lastMessage.type === 'image') return '📷 Photo';
  }
  return 'No messages yet';
};

/**
 * Safe href for opening an image in a new tab.
 * Only allows http(s) URLs — protects against javascript: URLs.
 */
const safeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  return trimmed;
};

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg =
    type === 'error' ? 'bg-red-500'
    : type === 'warning' ? 'bg-yellow-500'
    : 'bg-green-500';

  const icon =
    type === 'error' ? 'fa-circle-exclamation'
    : type === 'warning' ? 'fa-triangle-exclamation'
    : 'fa-circle-check';

  return (
    <div
      role="status"
      className={`fixed bottom-4 right-4 z-[80] px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-2 ${bg}`}
    >
      <i className={`fas ${icon}`} aria-hidden="true" />
      {message}
    </div>
  );
};

/* ================================================================== */
/* Confirm Dialog                                                      */
/* ================================================================== */

const ConfirmDialog = ({ open, title, message, confirmLabel = 'Delete', onConfirm, onCancel }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onCancel();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-sm w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
            <i className="fas fa-exclamation-triangle text-red-500" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-1">{title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Avatar                                                              */
/* ================================================================== */

const Avatar = ({ user, size = 'w-10 h-10', showStatus = false, isOnline = false }) => {
  const photoUrl = user?.photoUrl || user?.photoURL;
  const name = user?.displayName || user?.email || 'User';
  const initial = name.charAt(0).toUpperCase();

  return (
    <div className="relative inline-block flex-shrink-0">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          className={`rounded-full object-cover ${size}`}
        />
      ) : (
        <div
          className={`rounded-full bg-gradient-to-br from-orange to-orange-dark text-white flex items-center justify-center font-bold ${size}`}
        >
          {initial}
        </div>
      )}
      {showStatus && isOnline && (
        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full" />
      )}
    </div>
  );
};

/* ================================================================== */
/* Search Input                                                        */
/* ================================================================== */

const SearchInput = ({ value, onChange, placeholder = 'Search...' }) => (
  <div className="relative">
    <i
      className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
      aria-hidden="true"
    />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange dark:bg-gray-700 dark:text-white text-sm"
    />
  </div>
);

/* ================================================================== */
/* Chat List Item                                                      */
/* ================================================================== */

const ChatListItem = ({
  chat,
  isActive,
  onClick,
  currentUserId,
  usersList,
  getUserOnlineStatus,
}) => {
  const isPrivate = chat.type === 'private';
  let displayName = chat.name;
  let avatarUser = null;
  let isOnline = false;

  if (isPrivate) {
    const otherId = chat.participants?.find((id) => id !== currentUserId);
    const otherUser = usersList.find((u) => u.uid === otherId);
    displayName = otherUser?.displayName || otherUser?.email || 'User';
    avatarUser = otherUser;
    isOnline = getUserOnlineStatus?.(otherUser?.uid) || false;
  } else {
    avatarUser = { photoUrl: chat.groupPhoto, displayName: chat.name };
  }

  // Normalize lastMessage to a string preview, tolerating string / object / null
  const lastMessage = formatLastMessagePreview(chat.lastMessage);

  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 cursor-pointer transition-all duration-200 border-l-4 ${
        isActive
          ? 'bg-orange/10 border-orange'
          : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      <Avatar user={avatarUser} size="w-12 h-12" showStatus={isPrivate} isOnline={isOnline} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline gap-2">
          <h4 className="font-semibold text-gray-900 dark:text-white truncate">
            {displayName}
          </h4>
          {chat.lastUpdated && (
            <span className="text-[10px] text-gray-400 flex-shrink-0">
              {formatTime(chat.lastUpdated)}
            </span>
          )}
        </div>
        <div className="flex justify-between items-center gap-2">
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {lastMessage}
          </p>
          {chat.unreadCounts?.[currentUserId] > 0 && (
            <span className="ml-2 bg-orange text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center flex-shrink-0">
              {chat.unreadCounts[currentUserId] > 9
                ? '9+'
                : chat.unreadCounts[currentUserId]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Chat Message                                                        */
/* ================================================================== */

const ChatMessage = ({
  message,
  isOwn,
  onEdit,
  onDelete,
  isEditing,
  editText,
  setEditText,
  saveEdit,
  cancelEdit,
  activeChatId,
  usersList,
  reactionOptions,
  addReaction,
}) => {
  const senderUser = usersList.find((u) => u.uid === message.senderId);
  const senderName = isOwn
    ? 'You'
    : senderUser?.displayName || senderUser?.email || 'User';
  const reactionCounts = getReactionCounts(message.reactions);
  const hasReactions = Object.keys(reactionCounts).length > 0;
  const imageHref = safeImageUrl(message.imageUrl);

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}>
      <div
        className={`max-w-[75%] sm:max-w-[70%] ${
          isOwn
            ? 'bg-orange text-white'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white'
        } rounded-2xl px-3 py-2 shadow-sm relative`}
      >
        {/* Sender name for group chats */}
        {!isOwn && activeChatId?.type === 'group' && (
          <div className="text-xs font-semibold text-orange mb-1">{senderName}</div>
        )}

        {/* Message content */}
        {message.deleted ? (
          <div
            className={`text-xs italic ${
              isOwn ? 'text-orange-200' : 'text-gray-400'
            }`}
          >
            <i className="fas fa-ban mr-1" aria-hidden="true" /> This message was deleted
          </div>
        ) : isEditing ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEdit();
                if (e.key === 'Escape') cancelEdit();
              }}
              className="bg-white dark:bg-gray-600 text-black dark:text-white px-2 py-1 rounded text-sm flex-1 min-w-0"
              autoFocus
            />
            <button onClick={saveEdit} className="text-green-500 p-1" aria-label="Save">
              <i className="fas fa-check" aria-hidden="true" />
            </button>
            <button onClick={cancelEdit} className="text-red-500 p-1" aria-label="Cancel">
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <>
            {message.imageUrl && (
              <img
                src={message.imageUrl}
                alt="Shared"
                className={`max-w-full rounded-lg max-h-60 object-contain mb-1 ${
                  imageHref ? 'cursor-pointer hover:opacity-90 transition' : ''
                }`}
                onClick={() => {
                  if (imageHref) window.open(imageHref, '_blank', 'noopener,noreferrer');
                }}
              />
            )}
            {message.text && (
              <p className="text-sm break-words whitespace-pre-wrap">{message.text}</p>
            )}
          </>
        )}

        {/* Message footer */}
        <div
          className={`text-[10px] mt-1 ${
            isOwn ? 'text-orange-100' : 'text-gray-400'
          } flex items-center justify-end gap-2`}
        >
          {message.edited && !isEditing && <span className="italic">edited</span>}
          <span title={formatTime(message.timestamp)}>
            {formatMessageTime(message.timestamp)}
          </span>
          {isOwn && message.readBy && message.readBy.length > 1 && !message.deleted && (
            <span className="text-green-300" title="Read">
              ✓✓
            </span>
          )}
        </div>

        {/* Reaction badges */}
        {hasReactions && !message.deleted && !isEditing && (
          <div className="flex flex-wrap gap-1 mt-2">
            {Object.entries(reactionCounts).map(([emoji, count]) => (
              <div
                key={emoji}
                className="inline-flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5 text-xs"
              >
                <span>{emoji}</span>
                <span>{count}</span>
              </div>
            ))}
          </div>
        )}

        {/* Reaction picker */}
        {!message.deleted && !isEditing && (
          <div className="absolute -right-2 -top-10 opacity-0 group-hover:opacity-100 transition-all duration-200 flex gap-1 bg-white dark:bg-gray-800 rounded-full shadow-md p-1 z-10">
            {reactionOptions.map((emoji) => (
              <button
                key={emoji.type}
                onClick={() => addReaction(activeChatId?.id, message.id, emoji.type)}
                className="text-sm hover:scale-125 transition-transform p-1"
                title={emoji.label}
              >
                {emoji.type}
              </button>
            ))}
          </div>
        )}

        {/* Edit/Delete for own messages */}
        {isOwn && !isEditing && !message.deleted && (
          <div className="absolute -left-10 top-1 opacity-0 group-hover:opacity-100 transition-all duration-200 flex gap-1">
            <button
              onClick={() => onEdit(message)}
              className="text-gray-500 hover:text-blue-500 text-xs p-1"
              aria-label="Edit"
              title="Edit"
            >
              <i className="fas fa-pen" aria-hidden="true" />
            </button>
            <button
              onClick={() => onDelete(message.id)}
              className="text-gray-500 hover:text-red-500 text-xs p-1"
              aria-label="Delete"
              title="Delete"
            >
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ================================================================== */
/* Chat Page                                                           */
/* ================================================================== */

const Chat = () => {
  const { user } = useAuth();
  const {
    chats,
    loading,
    activeChat,
    setActiveChat,
    messages,
    messagesLoading,
    sendMessage,
    createPrivateChat,
    createGroupChat,
    usersList,
    deleteChat,
    editMessage,
    deleteMessage,
    getUserOnlineStatus,
    startTyping,
    stopTyping,
    typingUsers,
    sendImage,
    markChatAsRead,
    addReaction,
    addGroupMembers,
  } = useChat();

  const { startCall } = useVideoCall();

  /* ---------------- State ---------------- */
  const [newMessage, setNewMessage] = useState('');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMobileChatList, setShowMobileChatList] = useState(true);
  const [toast, setToast] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });

  /* ---------------- Refs ---------------- */
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  // Tracks whether the user is currently near the bottom so we know
  // whether to auto-scroll on new messages.
  const isNearBottomRef = useRef(true);

  /* ---------------- Reaction options ---------------- */
  const reactionOptions = [
    { type: '👍', label: 'Like' },
    { type: '❤️', label: 'Heart' },
    { type: '😂', label: 'Laugh' },
    { type: '😮', label: 'Wow' },
    { type: '🎉', label: 'Celebrate' },
    { type: '👏', label: 'Clap' },
  ];

  /* ---------------- Debounced search ---------------- */
  const debouncedSearch = useDebounced(searchTerm, 150);

  /* ---------------- Auto-scroll to bottom ---------------- */
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // Auto-scroll only when the user is already near the bottom.
  // This prevents yanking the user down while they're reading history.
  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // When switching chats, snap to bottom and reset transient edit state.
  useEffect(() => {
    isNearBottomRef.current = true;
    lastMessageCountRef.current = 0;
    setEditingMessageId(null);
    setEditText('');
    setShowScrollButton(false);
    // Scroll after the new messages paint
    const t = setTimeout(() => scrollToBottom('auto'), 30);
    return () => clearTimeout(t);
  }, [activeChat?.id, scrollToBottom]);

  /* ---------------- Show scroll-to-bottom button ---------------- */
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const nearBottom = distFromBottom <= 200;
      isNearBottomRef.current = nearBottom;
      setShowScrollButton(!nearBottom);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [activeChat?.id]);

  /* ---------------- Mark as read when active ---------------- */
  useEffect(() => {
    if (activeChat) {
      markChatAsRead(activeChat.id);
    }
  }, [activeChat?.id, messages.length]); 

  /* ---------------- Document title unread count ---------------- */
  const totalUnread = useMemo(
    () =>
      chats.reduce(
        (sum, c) => sum + (c.unreadCounts?.[user?.uid] || 0),
        0,
      ),
    [chats, user?.uid],
  );

  useEffect(() => {
    const base = 'IIEE-ERCSA';
    document.title = totalUnread > 0 ? `(${totalUnread}) ${base}` : base;
    return () => {
      document.title = base;
    };
  }, [totalUnread]);

  /* ---------------- Mobile resize ---------------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setShowMobileChatList(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ---------------- Incoming message sound ---------------- */
  const playIncomingSound = useCallback(() => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const playBeep = () => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.frequency.value = 600;
        gain.gain.value = 0.08;
        oscillator.start();
        gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.4);
        oscillator.stop(ctx.currentTime + 0.4);
      };
      if (ctx.state === 'suspended') ctx.resume().then(playBeep).catch(() => {});
      else playBeep();
    } catch (err) {
      console.warn('Could not play incoming sound', err);
    }
  }, []);

  useEffect(() => {
    if (!messages.length) {
      lastMessageCountRef.current = 0;
      return;
    }
    const currentCount = messages.length;
    const lastCount = lastMessageCountRef.current;
    if (currentCount > lastCount) {
      const newestMsg = messages[messages.length - 1];
      if (newestMsg && newestMsg.senderId !== user?.uid && document.hidden) {
        playIncomingSound();
      }
    }
    lastMessageCountRef.current = currentCount;
  }, [messages, user, playIncomingSound]);

  // Close AudioContext on unmount to release the resource
  useEffect(() => {
    return () => {
      const ctx = audioContextRef.current;
      if (ctx && ctx.state !== 'closed') {
        try { ctx.close?.(); } catch { /* noop */ }
      }
      audioContextRef.current = null;
    };
  }, []);

  /* ---------------- Handlers ---------------- */
  const handleSetActiveChat = async (chat) => {
    setActiveChat(chat);
    await markChatAsRead(chat.id);
    setShowMobileChatList(false);
  };

  const handleBackToList = () => {
    setActiveChat(null);
    setShowMobileChatList(true);
  };

  const handleDeleteChat = (chatId) => {
    setConfirmState({
      title: 'Delete conversation?',
      message: 'This entire conversation will be permanently removed. Cannot be undone.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        await deleteChat(chatId);
        if (activeChat?.id === chatId) {
          setActiveChat(null);
          setShowMobileChatList(true);
        }
        setConfirmState(null);
        showToast('Conversation deleted');
      },
    });
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeChat) return;
    stopTyping(activeChat.id);
    try {
      await sendMessage(activeChat.id, newMessage);
      setNewMessage('');
      // Sending a message should always scroll us to the bottom
      isNearBottomRef.current = true;
    } catch (err) {
      console.error('Send failed:', err);
      showToast('Failed to send message', 'error');
    }
  };

  const handleTyping = () => {
    if (!activeChat) return;
    startTyping(activeChat.id);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(activeChat.id);
    }, 2000);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChat) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('Image size should be less than 10MB', 'error');
      return;
    }

    setUploadingImage(true);
    try {
      await sendImage(activeChat.id, file);
      isNearBottomRef.current = true;
    } catch (err) {
      console.error('Image upload failed:', err);
      showToast('Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleEditMessage = (msg) => {
    setEditingMessageId(msg.id);
    setEditText(msg.text || '');
  };

  const saveEdit = async () => {
    if (!editText.trim() || !activeChat || !editingMessageId) return;
    try {
      await editMessage(activeChat.id, editingMessageId, editText);
      setEditingMessageId(null);
      setEditText('');
    } catch (err) {
      console.error('Edit failed:', err);
      showToast('Failed to edit message', 'error');
    }
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  const handleDeleteMessage = (messageId) => {
    setConfirmState({
      title: 'Delete message?',
      message: 'This message will be replaced with "deleted" for everyone.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        await deleteMessage(activeChat.id, messageId);
        setConfirmState(null);
      },
    });
  };

  const startPrivateChat = async (otherUserId) => {
    try {
      const chat = await createPrivateChat(otherUserId);
      if (chat) handleSetActiveChat(chat);
      setShowAllUsers(false);
    } catch (err) {
      console.error('Chat creation error:', err);
      showToast('Could not start chat. Please try again.', 'error');
    }
  };

  const handleVideoCall = async () => {
    if (!activeChat || activeChat.type !== 'private') return;

    const otherId = activeChat.participants.find((id) => id !== user.uid);
    const otherUser = usersList.find((u) => u.uid === otherId);
    if (otherUser) {
      try {
        await startCall(otherId, otherUser.displayName || otherUser.email);
      } catch (err) {
        console.error('Video call error:', err);
        showToast('Unable to start video call. Check permissions.', 'error');
      }
    }
  };

  /* ---------------- Filtered chats ---------------- */
  const filteredChats = useMemo(() => {
    if (!debouncedSearch.trim()) return chats;
    const q = debouncedSearch.toLowerCase();
    return chats.filter((chat) => {
      if (chat.type === 'private') {
        const otherId = chat.participants?.find((id) => id !== user.uid);
        const otherUser = usersList.find((u) => u.uid === otherId);
        const name = otherUser?.displayName || otherUser?.email || 'User';
        return name.toLowerCase().includes(q);
      }
      return chat.name?.toLowerCase().includes(q);
    });
  }, [chats, debouncedSearch, usersList, user?.uid]);

  /* ---------------- Derived ---------------- */
  const typingMessage =
    typingUsers.length > 0 && activeChat
      ? typingUsers.length === 1
        ? `${usersList.find((u) => u.uid === typingUsers[0])?.displayName || 'Someone'} is typing...`
        : `${typingUsers.length} people are typing...`
      : null;

  const otherUser =
    activeChat?.type === 'private' && activeChat.participants
      ? usersList.find(
          (u) => u.uid === activeChat.participants.find((id) => id !== user.uid),
        )
      : null;

  const isOnline = otherUser ? getUserOnlineStatus(otherUser.uid) : false;

  /* ---------------- Auth gate ---------------- */
  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg max-w-sm">
          <i className="fas fa-lock text-4xl text-orange mb-4" aria-hidden="true" />
          <h2 className="text-2xl font-bold mb-2 text-gray-900 dark:text-white">
            Login Required
          </h2>
          <p className="mb-6 text-gray-600 dark:text-gray-400">
            Please log in to access messages.
          </p>
          <Link
            to="/login"
            className="inline-block bg-orange text-white px-6 py-2 rounded-lg hover:bg-orange-dark transition"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  /* ---------------- Render ---------------- */
  return (
    <div className="h-[calc(100vh-4rem)] bg-gray-100 dark:bg-gray-900">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
      />

      <div className="container mx-auto px-2 sm:px-4 py-3 h-full max-w-6xl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden flex h-full">
          {/* ========== Chat List Sidebar ========== */}
          <div
            className={`${
              showMobileChatList ? 'flex' : 'hidden'
            } md:flex w-full md:w-80 flex-col border-r border-gray-200 dark:border-gray-700`}
          >
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Messages
                </h2>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => setShowAllUsers(true)}
                    className="bg-orange text-white p-2 rounded-full hover:bg-orange-dark transition"
                    title="New chat"
                    aria-label="Start new chat"
                  >
                    <i className="fas fa-plus text-sm" aria-hidden="true" />
                  </button>
                  <button
                    onClick={() => setShowCreateGroup(true)}
                    className="bg-orange text-white p-2 rounded-full hover:bg-orange-dark transition"
                    title="New group"
                    aria-label="Create new group"
                  >
                    <i className="fas fa-users text-sm" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search conversations..."
              />
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-orange border-t-transparent" />
                </div>
              ) : filteredChats.length === 0 ? (
                <div className="text-center py-8 px-4 text-gray-500 dark:text-gray-400">
                  <i className="fas fa-comments text-4xl mb-2 opacity-50" aria-hidden="true" />
                  <p className="text-sm">
                    {searchTerm ? 'No matches found' : 'No conversations yet'}
                  </p>
                  {!searchTerm && (
                    <p className="text-xs mt-1">Click + to start a new chat</p>
                  )}
                </div>
              ) : (
                filteredChats.map((chat) => (
                  <ChatListItem
                    key={chat.id}
                    chat={chat}
                    isActive={activeChat?.id === chat.id}
                    onClick={() => handleSetActiveChat(chat)}
                    currentUserId={user.uid}
                    usersList={usersList}
                    getUserOnlineStatus={getUserOnlineStatus}
                  />
                ))
              )}
            </div>
          </div>

          {/* ========== Chat Area ========== */}
          <div
            className={`flex-1 flex flex-col min-w-0 ${
              !showMobileChatList ? 'flex' : 'hidden md:flex'
            }`}
          >
            {activeChat ? (
              <>
                {/* Header */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      onClick={handleBackToList}
                      className="md:hidden text-gray-500 hover:text-orange transition p-1"
                      aria-label="Back"
                    >
                      <i className="fas fa-arrow-left text-lg" aria-hidden="true" />
                    </button>

                    <Avatar
                      user={otherUser || { displayName: activeChat.name }}
                      size="w-10 h-10"
                      showStatus={activeChat.type === 'private'}
                      isOnline={isOnline}
                    />

                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {activeChat.type === 'private'
                          ? otherUser?.displayName || otherUser?.email || 'User'
                          : activeChat.name}
                      </h3>
                      <div className="text-xs">
                        {activeChat.type === 'private' ? (
                          <span
                            className={isOnline ? 'text-green-500' : 'text-gray-400'}
                          >
                            {isOnline ? '● Online' : 'Offline'}
                          </span>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400">
                            <i className="fas fa-users mr-1" aria-hidden="true" />
                            {activeChat.participants?.length || 0} members
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-1 flex-shrink-0">
                    {activeChat.type === 'private' && (
                      <button
                        onClick={handleVideoCall}
                        className="p-2 text-gray-500 hover:text-orange rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                        title="Video call"
                        aria-label="Start video call"
                      >
                        <i className="fas fa-video" aria-hidden="true" />
                      </button>
                    )}
                    {activeChat.type === 'group' && (
                      <button
                        onClick={() => setShowAddMembersModal(true)}
                        className="p-2 text-gray-500 hover:text-orange rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                        title="Add members"
                        aria-label="Add members"
                      >
                        <i className="fas fa-user-plus" aria-hidden="true" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteChat(activeChat.id)}
                      className="p-2 text-gray-500 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                      title="Delete chat"
                      aria-label="Delete conversation"
                    >
                      <i className="fas fa-trash-alt" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div
                  ref={messagesContainerRef}
                  className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-gray-50 dark:bg-gray-900 relative"
                >
                  {messagesLoading && (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-orange border-t-transparent" />
                    </div>
                  )}

                  {messages.length === 0 && !messagesLoading && (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
                      <i
                        className="fas fa-comment-dots text-5xl mb-3 opacity-50"
                        aria-hidden="true"
                      />
                      <p className="text-sm">No messages yet</p>
                      <p className="text-xs">Send a message to start the conversation</p>
                    </div>
                  )}

                  {messages.map((msg, index) => (
                    <ChatMessage
                      key={`${msg.id}-${index}`}
                      message={msg}
                      isOwn={msg.senderId === user.uid}
                      onEdit={handleEditMessage}
                      onDelete={handleDeleteMessage}
                      isEditing={editingMessageId === msg.id}
                      editText={editText}
                      setEditText={setEditText}
                      saveEdit={saveEdit}
                      cancelEdit={cancelEdit}
                      activeChatId={activeChat}
                      usersList={usersList}
                      reactionOptions={reactionOptions}
                      addReaction={addReaction}
                    />
                  ))}

                  {/* Typing indicator */}
                  {typingMessage && (
                    <div className="flex justify-start">
                      <div className="bg-gray-200 dark:bg-gray-700 rounded-2xl px-4 py-2 flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
                          <div
                            className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                            style={{ animationDelay: '0.15s' }}
                          />
                          <div
                            className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"
                            style={{ animationDelay: '0.3s' }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-300">
                          {typingMessage}
                        </span>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Scroll to bottom */}
                {showScrollButton && (
                  <button
                    onClick={() => {
                      isNearBottomRef.current = true;
                      scrollToBottom();
                    }}
                    className="absolute bottom-24 right-6 bg-white dark:bg-gray-700 shadow-lg rounded-full w-10 h-10 flex items-center justify-center text-orange hover:bg-orange hover:text-white transition z-10"
                    aria-label="Scroll to latest"
                  >
                    <i className="fas fa-arrow-down" aria-hidden="true" />
                  </button>
                )}

                {/* Message input */}
                <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="flex gap-2 items-center">
                    <label className="cursor-pointer text-gray-500 hover:text-orange p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                      <i className="fas fa-image text-lg" aria-hidden="true" />
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>

                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      onKeyUp={handleTyping}
                      placeholder={`Message ${
                        activeChat.type === 'private'
                          ? otherUser?.displayName || otherUser?.email || 'User'
                          : activeChat.name
                      }...`}
                      className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-orange dark:bg-gray-700 dark:text-white text-sm"
                      disabled={uploadingImage}
                    />

                    <button
                      onClick={handleSend}
                      disabled={!newMessage.trim() || uploadingImage}
                      className="bg-orange text-white p-2.5 rounded-full hover:bg-orange-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Send"
                    >
                      <i className="fas fa-paper-plane" aria-hidden="true" />
                    </button>
                  </div>

                  {uploadingImage && (
                    <div className="text-center text-xs text-orange mt-2">
                      <i className="fas fa-spinner fa-pulse mr-1" aria-hidden="true" />
                      Uploading image...
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">
                <div className="text-center px-4">
                  <i
                    className="fas fa-comments text-5xl sm:text-6xl mb-4 opacity-30"
                    aria-hidden="true"
                  />
                  <p className="text-base sm:text-lg">Select a conversation</p>
                  <p className="text-sm">or start a new one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateGroup && (
        <CreateGroupModal
          onClose={() => setShowCreateGroup(false)}
          onCreate={createGroupChat}
          usersList={usersList}
          currentUserId={user.uid}
        />
      )}

      {showAllUsers && (
        <AllUsersModal
          onClose={() => setShowAllUsers(false)}
          usersList={usersList}
          currentUserId={user.uid}
          onStartChat={startPrivateChat}
          getUserOnlineStatus={getUserOnlineStatus}
        />
      )}

      {showAddMembersModal && activeChat && activeChat.type === 'group' && (
        <AddGroupMembersModal
          onClose={() => setShowAddMembersModal(false)}
          usersList={usersList}
          currentUserId={user.uid}
          currentParticipants={activeChat.participants}
          onAddMembers={addGroupMembers}
        />
      )}

      {/* Video Call */}
      <VideoCall />
    </div>
  );
};

/* ================================================================== */
/* Modal Components                                                    */
/* ================================================================== */

const CreateGroupModal = ({ onClose, onCreate, usersList, currentUserId }) => {
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounced(searchTerm, 150);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filteredUsers = useMemo(() => {
    if (!debouncedSearch.trim()) return usersList;
    const q = debouncedSearch.toLowerCase();
    return usersList.filter((u) =>
      (u.displayName || u.email || '').toLowerCase().includes(q),
    );
  }, [usersList, debouncedSearch]);

  const toggleUser = (uid) => {
    setSelectedUsers((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid],
    );
  };

  const handleSubmit = () => {
    if (!groupName.trim() || selectedUsers.length === 0) return;
    onCreate(groupName, selectedUsers);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Create Group Chat
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        <input
          type="text"
          placeholder="Group name"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          className="w-full p-2 border border-gray-200 dark:border-gray-700 rounded-lg mb-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
          autoFocus
        />

        <input
          type="text"
          placeholder="Search members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 border border-gray-200 dark:border-gray-700 rounded-lg mb-3 dark:bg-gray-700 dark:text-white"
        />

        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {selectedUsers.map((uid) => {
              const u = usersList.find((x) => x.uid === uid);
              if (!u) return null;
              return (
                <span
                  key={uid}
                  className="bg-orange/10 text-orange text-xs px-2 py-1 rounded-full flex items-center gap-1"
                >
                  {u.displayName || u.email}
                  <button onClick={() => toggleUser(uid)} aria-label="Remove">
                    <i className="fas fa-times text-[10px]" aria-hidden="true" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="max-h-60 overflow-y-auto mb-4 space-y-1">
          {filteredUsers.map((u) => (
            <div
              key={u.uid}
              onClick={() => toggleUser(u.uid)}
              className="flex items-center gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition"
            >
              <input
                type="checkbox"
                checked={selectedUsers.includes(u.uid)}
                readOnly
                className="w-4 h-4 accent-orange"
              />
              <Avatar user={u} size="w-8 h-8" />
              <span className="text-gray-900 dark:text-white truncate">
                {u.displayName || u.email}
              </span>
            </div>
          ))}
          {filteredUsers.length === 0 && (
            <div className="text-center text-gray-500 py-4">No users found</div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={!groupName.trim() || selectedUsers.length === 0}
            className="flex-1 bg-orange text-white py-2 rounded-lg hover:bg-orange-dark transition disabled:opacity-50"
          >
            Create ({selectedUsers.length})
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const AllUsersModal = ({
  onClose,
  usersList,
  currentUserId,
  onStartChat,
  getUserOnlineStatus,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounced(searchTerm, 150);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const list = usersList.filter((u) => u.uid !== currentUserId);
    if (!debouncedSearch.trim()) return list;
    const q = debouncedSearch.toLowerCase();
    return list.filter((u) =>
      (u.displayName || u.email || '').toLowerCase().includes(q),
    );
  }, [usersList, currentUserId, debouncedSearch]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full max-h-[80vh] flex flex-col p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Start New Chat
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 border border-gray-200 dark:border-gray-700 rounded-lg mb-4 dark:bg-gray-700 dark:text-white"
          autoFocus
        />

        <div className="flex-1 overflow-y-auto space-y-2">
          {filtered.map((u) => {
            const online = getUserOnlineStatus?.(u.uid) || false;
            return (
              <div
                key={u.uid}
                onClick={() => onStartChat(u.uid)}
                className="flex items-center gap-3 p-3 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl cursor-pointer transition"
              >
                <Avatar user={u} size="w-12 h-12" showStatus isOnline={online} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-white truncate">
                    {u.displayName || u.email}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {u.role || 'Member'}
                  </div>
                </div>
                <i className="fas fa-comment text-orange text-xl flex-shrink-0" aria-hidden="true" />
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center text-gray-500 py-8">No users found</div>
          )}
        </div>
      </div>
    </div>
  );
};

const AddGroupMembersModal = ({
  onClose,
  usersList,
  currentUserId,
  currentParticipants,
  onAddMembers,
}) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounced(searchTerm, 150);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const availableUsers = useMemo(
    () =>
      usersList.filter(
        (u) => !currentParticipants.includes(u.uid) && u.uid !== currentUserId,
      ),
    [usersList, currentParticipants, currentUserId],
  );

  const filteredUsers = useMemo(() => {
    if (!debouncedSearch.trim()) return availableUsers;
    const q = debouncedSearch.toLowerCase();
    return availableUsers.filter((u) =>
      (u.displayName || u.email || '').toLowerCase().includes(q),
    );
  }, [availableUsers, debouncedSearch]);

  const toggleUser = (uid) => {
    setSelectedUsers((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid],
    );
  };

  const handleSubmit = () => {
    if (selectedUsers.length === 0) return;
    onAddMembers(selectedUsers);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            Add Members
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        <input
          type="text"
          placeholder="Search members..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 border border-gray-200 dark:border-gray-700 rounded-lg mb-4 dark:bg-gray-700 dark:text-white"
        />

        <div className="max-h-60 overflow-y-auto mb-4 space-y-1">
          {filteredUsers.map((u) => (
            <div
              key={u.uid}
              onClick={() => toggleUser(u.uid)}
              className="flex items-center gap-3 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition"
            >
              <input
                type="checkbox"
                checked={selectedUsers.includes(u.uid)}
                readOnly
                className="w-4 h-4 accent-orange"
              />
              <Avatar user={u} size="w-8 h-8" />
              <span className="text-gray-900 dark:text-white truncate">
                {u.displayName || u.email}
              </span>
            </div>
          ))}
          {filteredUsers.length === 0 && (
            <div className="text-center text-gray-500 py-4">
              No available members
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSubmit}
            disabled={selectedUsers.length === 0}
            className="flex-1 bg-orange text-white py-2 rounded-lg hover:bg-orange-dark transition disabled:opacity-50"
          >
            Add ({selectedUsers.length})
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;